import React, { useEffect, useState } from "react";
import MultiSelectBox from "../../../../../components/FormComponents/MultiSelectBox";
import SingleSelectBox from "../../../../../components/FormComponents/SingleSelectBox";
import useFetchData from "../../../../../hooks/useFetchData";
import Input from "../../../../../components/FormComponents/Input";
import { getLabelForKey } from "../../../../../utils/Config/DefaultConfig/formFieldConfig";
import Button from "../../../../../components/FormComponents/Button";
import MaterialIcon from "../../../../../components/BuilderComponents/MaterialIcon";
import Label from "../../../../../components/FormComponents/Label";
import { mapArrayToObject } from "../../../../../commonFunctions/arrayFunc";
import BuilderTable from "../../../../../Layouts/BuilderTable";

import VariableMapping from "../../../../../components/BuilderComponents/NewJobConfiguration/VariableMapping/VariableMapping";
import { extractVariables } from "../../../../../commonFunctions/fileFolderConfig";

const EditMasterDataLookup = ({ editFieldData, setEditFieldData, fields, tableID }) => {
  const { data, loading, error } = useFetchData("/rdbms");
  const {
    data: templateData,
    loading: templatesLoading,
    error: templatesError
  } = useFetchData("/http-request-templates");
  // const { data: connectorData, loading: connectorLoading, error: connectorError } = useFetchData("/dbconnector");

  useEffect(() => {
    setEditFieldData((prev) => ({
      ...prev,
      data_source: {
        type: prev.data_source?.type || "table",
        http_template_id: prev.data_source?.http_template_id || "",
        body_variable_mapping: prev.data_source?.body_variable_mapping || []
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTemplate = templateData?.find((t) => t.id === editFieldData.data_source?.http_template_id);

  const extractVariablesFromArray = (arrayData) => {
    if (!selectedTemplate || !Array.isArray(arrayData)) return [];

    const allVariableNames = new Set();

    arrayData.forEach((item) => {
      if (!item?.value) return;
      const newVars = extractVariables(item.value);
      newVars.forEach((name) => allVariableNames.add(name));
    });

    if (allVariableNames.size === 0) return [];

    return Array.from(allVariableNames);
  };

  const mergeParams = (newParams, existingParams) => {
    if (!newParams || newParams.length === 0) return [];

    if (!existingParams || existingParams.length === 0) {
      return newParams.map((name) => ({
        key: name,
        type: "mapped",
        value: ""
      }));
    }

    const existingMap = new Map(existingParams.map((param) => [param.key, param]));

    return newParams.map((key) => {
      const existing = existingMap.get(key);
      if (existing) {
        return existing;
      }
      return {
        key: key,
        type: "mapped",
        value: ""
      };
    });
  };

  useEffect(() => {
    if (!selectedTemplate) return;

    let bodyParams;

    if (selectedTemplate?.body_raw_key_values?.length) {
      bodyParams = extractVariablesFromArray(selectedTemplate.body_raw_key_values);
    }

    setEditFieldData((prev) => ({
      ...prev,
      data_source: {
        ...prev.data_source,
        body_variable_mapping: mergeParams(bodyParams, prev.data_source?.body_variable_mapping)
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Something went wrong</div>;
  if (templatesLoading) return <div>Templates Loading...</div>;
  if (templatesError) return <div>Something went wrong</div>;

  const handleDataSourceChange = (type) => {
    setEditFieldData((prev) => ({
      ...prev,
      masterdata_type: type === "http-template" ? "http-template" : "rdbms",
      data_source: {
        ...(prev.data_source || {}),
        type
      }
    }));
  };

  const handleChange = (key, value) => {
    if (key === "masterdata_id" && value === "") {
      // Reset dependent fields when masterdata_id is cleared
      setEditFieldData({
        ...editFieldData,
        masterdata_id: "",
        selected_display_field: null,
        search_fields: [],
        display_fields: [],
        info_fields: [],
        data_source: {
          type: "table",
          http_template_id: ""
        }
      });
    } else {
      setEditFieldData({
        ...editFieldData,
        [key]: value
      });
    }
  };

  const handleVariableMappingChange = (key, updatedData) => {
    setEditFieldData((prev) => ({
      ...prev,
      data_source: {
        ...prev.data_source,
        [key]: updatedData
      }
    }));
  };

  const handleChangeValidationInput = (name, value) => {
    setEditFieldData({
      ...editFieldData,
      validate: { ...editFieldData.validate, [name]: value }
    });
  };

  const handleTemplateChange = (id) => {
    setEditFieldData((prev) => ({
      ...prev,
      data_source: {
        ...prev.data_source,
        http_template_id: id,
        body_variable_mapping: []
      },
      selected_display_field: null,
      search_fields: [],
      display_fields: [],
      info_fields: [],
      rdbms_to_form_map: []
    }));
  };

  const formattedTemplateData =
    templateData?.map((t) => ({
      ...t,
      fields: t.http_output_variable_bindings?.map((b) => ({ name: b.name })) || []
    })) || [];

  const isTableSource = !editFieldData?.data_source?.type || editFieldData?.data_source?.type === "table";

  return (
    <div className="flex_column gap_3">
      <Input
        label={getLabelForKey("label")}
        value={editFieldData.label}
        name="label"
        onChange={({ target }) => handleChange(target.name, target.value)}
      />
      <Input
        label={getLabelForKey("form_display_label")}
        value={editFieldData.form_display_label}
        name="form_display_label"
        onChange={({ target }) => handleChange(target.name, target.value)}
      />
      <Input
        label={getLabelForKey("custom_width_px")}
        value={editFieldData.custom_width || ""}
        name="custom_width"
        type="number"
        onChange={({ target }) => handleChange(target.name, target.value)}
      />

      <div className="d_flex">
        <Input
          type="checkbox"
          label={getLabelForKey("required")}
          checked={editFieldData.validate.required}
          name="required"
          onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
          containerClassName="w_50"
        />
        <Input
          type="checkbox"
          label={getLabelForKey("disable")}
          checked={editFieldData.disable}
          name="disable"
          onChange={({ target }) => handleChange(target.name, target.checked)}
          containerClassName="w_50"
        />
      </div>

      <div className="d_flex">
        <Input
          type="checkbox"
          label={getLabelForKey("mandatory")}
          checked={editFieldData.validate.mandatory}
          name="mandatory"
          onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
          containerClassName="w_50"
        />
        <Input
          type="checkbox"
          label={getLabelForKey("backend_validation")}
          checked={editFieldData.validate.backend_validation}
          name="backend_validation"
          size="medium"
          containerClassName="w_50"
          onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
        />
      </div>

      {/*<SingleSelectBox*/}
      {/*  options={[{ id: "", name: "None" }, ...(connectorData || [])]}*/}
      {/*  selectedData={editFieldData.db_connector || ""}*/}
      {/*  onChange={(obj) => handleChange("db_connector", obj.id)}*/}
      {/*  label="Select DB Connector"*/}
      {/*/>*/}

      <SingleSelectBox
        options={[
          { id: "table", name: "Table" },
          { id: "http-template", name: "HTTP Template" }
        ]}
        selectedData={editFieldData?.data_source?.type || "table"}
        onChange={(obj) => handleDataSourceChange(obj.id)}
        label="Select Data Source"
      />

      {isTableSource && (
        <>
          <SingleSelectBox
            options={[{ id: "", name: "None" }, ...data]}
            selectedData={editFieldData.masterdata_id}
            onChange={(obj) => handleChange("masterdata_id", obj.id)}
            label="Select Table"
          />

          <SingleSelectBox
            options={[{ id: "", name: "None" }, ...data]}
            selectedData={editFieldData.remember_rdbms_table_id}
            onChange={(obj) => handleChange("remember_rdbms_table_id", obj.id)}
            label="Select Remember RDBMS Table"
          />

          <RdbmsReferenceFields
            rdbmsData={data}
            editFieldData={editFieldData}
            handleChange={handleChange}
            formFields={fields}
            isTableSource={isTableSource}
          />
        </>
      )}

      {editFieldData?.data_source?.type === "http-template" && (
        <>
          <SingleSelectBox
            options={formattedTemplateData}
            selectedData={editFieldData.data_source?.http_template_id}
            onChange={(obj) => handleTemplateChange(obj.id)}
            label="Select HTTP Template"
          />

          {selectedTemplate && (
            <VariableMapping
              variableData={editFieldData.data_source?.body_variable_mapping}
              onChange={(updatedData) => handleVariableMappingChange("body_variable_mapping", updatedData)}
              label="Body Variable Mapping"
              formFields={fields}
              tableId={tableID}
            />
          )}

          <RdbmsReferenceFields
            rdbmsData={formattedTemplateData}
            editFieldData={editFieldData}
            handleChange={handleChange}
            formFields={fields}
            selectedId={editFieldData.data_source?.http_template_id}
          />
        </>
      )}

      <SingleSelectBox
        options={fields?.filter((field) => field.type === "json") || []}
        labelKey={["label"]}
        selectedData={editFieldData.extension_field}
        onChange={(obj) => handleChange("extension_field", { id: obj.id, type: obj.type, label: obj.label })}
        label="Select Extension Field"
      />

      <Input
        label={getLabelForKey("help_text")}
        value={editFieldData.help_text}
        name="help_text"
        onChange={({ target }) => handleChange(target.name, target.value)}
      />
    </div>
  );
};

const RdbmsReferenceFields = ({ rdbmsData, editFieldData, handleChange, formFields, selectedId, isTableSource }) => {
  const lookupId = selectedId !== undefined ? selectedId : editFieldData.masterdata_id;
  const selectedRdbms = rdbmsData.find((rdbms) => rdbms.id === lookupId);

  if (!selectedRdbms) {
    return <div>Select Data Source first</div>;
  }
  if (selectedRdbms && (!selectedRdbms.fields || selectedRdbms.fields.length === 0)) {
    return <div>No fields available</div>;
  }
  return (
    <div className="flex_column gap_3">
      <SingleSelectBox
        valueKey="name"
        options={selectedRdbms?.fields || []}
        selectedData={editFieldData.selected_display_field || null}
        onChange={(obj) => handleChange("selected_display_field", obj)}
        label="Selected Save Field"
      />
      <MultiSelectBox
        valueKey="name"
        options={selectedRdbms?.fields || []}
        selectedData={editFieldData.search_fields}
        onChange={(data) => handleChange("search_fields", data)}
        label="Search Field"
      />
      <MultiSelectBox
        valueKey="name"
        options={selectedRdbms?.fields || []}
        selectedData={editFieldData.display_fields}
        onChange={(data) => handleChange("display_fields", data)}
        label="Dropdown Display Field"
      />
      <MultiSelectBox
        valueKey="name"
        options={selectedRdbms?.fields || []}
        selectedData={editFieldData.access_fields}
        onChange={(data) => handleChange("access_fields", data)}
        label={"Access Fields"}
      />
      <MultiSelectBox
        valueKey="name"
        options={selectedRdbms?.fields || []}
        selectedData={editFieldData.info_fields}
        onChange={(data) => handleChange("info_fields", data)}
        label="Info Field"
      />
      <Input
        type="checkbox"
        label={getLabelForKey("hide_info_fields")}
        checked={editFieldData.hide_info_fields}
        name="hide_info_fields"
        onChange={({ target }) => handleChange(target.name, target.checked)}
        containerClassName="w_50"
      />
      {isTableSource && (
        <MasterdataLookupMapping
          mapping={editFieldData.rdbms_to_form_map}
          formFields={formFields}
          rdbmsFields={selectedRdbms?.fields || []}
          onChange={(data) => handleChange("rdbms_to_form_map", data)}
        />
      )}
    </div>
  );
};

const initMapState = {
  key: "",
  value: "",
  type: "mapped"
};
export const MasterdataLookupMapping = ({ mapping, onChange, formFields, rdbmsFields }) => {
  const [mapData, setMapData] = useState(initMapState);
  const [mapDataOfFields, setMapDataOfFields] = useState({});

  const handleAddMap = () => {
    if (mapData.key === "" || mapData.value === "") return;
    onChange([...mapping, mapData]);
    setMapData(initMapState);
  };

  const handleChange = (key, data) => {
    setMapData({ ...mapData, [key]: data });
  };

  const handleDeleteMap = (index) => {
    const mapDataCopy = [...mapping];
    mapDataCopy.splice(index, 1);
    onChange(mapDataCopy);
  };

  useEffect(() => {
    const { result: mapFormFields } = mapArrayToObject(formFields);
    const { result: mapRdbmsFields } = mapArrayToObject(rdbmsFields, "name");

    setMapDataOfFields({ mapFormFields, mapRdbmsFields });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Label>{getLabelForKey("rdbms_to_form_map")}</Label>
      <div className="border_full p_3">
        <div className="d_flex gap_2">
          <SingleSelectBox
            valueKey="name"
            options={rdbmsFields || []}
            selectedData={mapData.key}
            onChange={(obj) => handleChange("key", obj.name)}
            placeholder="Selected RDBMS Field"
            isSearch={true}
          />
          <SingleSelectBox
            valueKey="id"
            labelKey="label"
            options={formFields || []}
            selectedData={mapData.value}
            onChange={(obj) => handleChange("value", obj.id)}
            placeholder="Selected Form Field"
            isSearch={true}
          />
          <Button onClick={handleAddMap}>
            <MaterialIcon name="add" />
          </Button>
        </div>
        <BuilderTable
          data={mapping}
          style={{ fontSize: "12px", marginTop: "10px" }}
          columnStyles={{ Custom: { width: "100px" } }}
          customColumns={[
            {
              header: "RDBMS Field",
              render: (data) => {
                try {
                  return mapDataOfFields?.mapRdbmsFields?.[data.key]?.name || "N/A";
                } catch (err) {
                  return "Error";
                }
              }
            },
            {
              header: "Form Field",
              render: (data) => {
                try {
                  return mapDataOfFields?.mapFormFields?.[data.value]?.label || "N/A";
                } catch (err) {
                  return "Error";
                }
              }
            },
            {
              header: "Custom",
              render: (_, index) => {
                return (
                  <Button variant="delete" onClick={() => handleDeleteMap(index)}>
                    <MaterialIcon name={"delete"} />
                  </Button>
                );
              }
            }
          ]}
        />
      </div>
    </>
  );
};

export default EditMasterDataLookup;
